<template>
  <FileReport />

  <div class="registration-container">
    <!-- Progress Header (like student registration) -->
    <div class="progress-header">
      <div class="header-content">
        <div class="title-section">
          <!-- Back Button -->
          <div class="back-button" @click="handleBackClick">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z" />
            </svg>
          </div>

          <div class="title-content">
            <h1 class="main-title">Tutor Registration</h1>
            <p class="subtitle">Join our community of dedicated educators and make a difference!</p>
          </div>
        </div>

        <!-- Step Progress (2 steps) -->
        <div class="step-progress">
          <div class="step active">
            <span class="step-number">1</span>
          </div>
          <div class="step-line"></div>
          <div class="step">
            <span class="step-number">2</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Main Form Card -->
      <div class="form-card">
        <!-- Personal Information Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Personal Information</h3>
              <p>Tutors must be 18 years and above only</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="firstName">First Name <span class="required">*</span></label>
              <input
                id="firstName"
                v-model="firstName"
                type="text"
                required
                maxlength="50"
                :class="{ error: errors.firstName }"
                placeholder="Enter your first name"
                @input="validateNameField(firstName, 'firstName')"
                @blur="validateNameField(firstName, 'firstName')"
              />
              <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
            </div>

            <div class="form-group">
              <label for="middleName">Middle Name</label>
              <input
                id="middleName"
                v-model="middleName"
                type="text"
                maxlength="50"
                :class="{ error: errors.middleName }"
                placeholder="Enter your middle name"
                @input="validateNameField(middleName, 'middleName')"
                @blur="validateNameField(middleName, 'middleName')"
              />
              <span v-if="errors.middleName" class="error-message">{{ errors.middleName }}</span>
            </div>

            <div class="form-group">
              <label for="lastName">Last Name <span class="required">*</span></label>
              <input
                id="lastName"
                v-model="lastName"
                type="text"
                required
                maxlength="50"
                :class="{ error: errors.lastName }"
                placeholder="Enter your last name"
                @input="validateNameField(lastName, 'lastName')"
                @blur="validateNameField(lastName, 'lastName')"
              />
              <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
            </div>

            <div class="form-group">
              <label for="suffix">Suffix</label>
              <select
                id="suffix"
                v-model="suffix"
                :class="{ error: errors.suffix }"
                @change="validateSuffix"
                @blur="validateSuffix"
              >
                <option value="">Select suffix (optional)</option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
                <option value="V">V</option>
                <option value="PhD">PhD</option>
                <option value="MD">MD</option>
                <option value="Other">Other</option>
              </select>
              <span v-if="errors.suffix" class="error-message">{{ errors.suffix }}</span>
            </div>

            <div class="form-group">
              <label for="email">Email <span class="required">*</span></label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                maxlength="254"
                :class="{ error: errors.email }"
                placeholder="Enter your email address"
                @input="validateEmail"
                @blur="validateEmailOnBlur"
              />
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <div class="form-group">
              <label for="dateOfBirth">Date of Birth <span class="required">*</span></label>
              <div class="date-input-wrapper">
                <input
                  id="dateOfBirth"
                  v-model="dateOfBirth"
                  type="date"
                  min="1950-01-01"
                  max="2005-12-31"
                  required
                  :class="{ error: errors.dateOfBirth }"
                  @input="validateDateOfBirth"
                  @blur="validateDateOfBirth"
                />
              </div>
              <span v-if="errors.dateOfBirth" class="error-message">{{ errors.dateOfBirth }}</span>
            </div>

            <div class="form-group">
              <label for="password">Password <span class="required">*</span></label>
              <div class="password-input-container">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  maxlength="64"
                  :class="{ error: errors.password }"
                  placeholder="Create a strong password"
                  @input="
                    () => {
                      validatePassword();
                      if (confirmPassword) {
                        validateConfirmPassword();
                      }
                    }
                  "
                  @blur="
                    () => {
                      validatePassword();
                      if (confirmPassword) {
                        validateConfirmPassword();
                      }
                    }
                  "
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click.prevent="togglePasswordVisibility"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showPassword"
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
              <div v-if="password" class="password-requirements">
                <div
                  class="requirement"
                  :class="{ valid: password.length >= 8 && password.length <= 64 }"
                >
                  <span class="checkmark">{{
                    password.length >= 8 && password.length <= 64 ? "✓" : "○"
                  }}</span>
                  Between 8 and 64 characters
                </div>
                <div class="requirement" :class="{ valid: /[a-z]/.test(password) }">
                  <span class="checkmark">{{ /[a-z]/.test(password) ? "✓" : "○" }}</span>
                  One lowercase letter
                </div>
                <div class="requirement" :class="{ valid: /[A-Z]/.test(password) }">
                  <span class="checkmark">{{ /[A-Z]/.test(password) ? "✓" : "○" }}</span>
                  One uppercase letter
                </div>
                <div class="requirement" :class="{ valid: /[0-9]/.test(password) }">
                  <span class="checkmark">{{ /[0-9]/.test(password) ? "✓" : "○" }}</span>
                  One number
                </div>
                <div class="requirement" :class="{ valid: hasSpecialChar(password) }">
                  <span class="checkmark">{{ hasSpecialChar(password) ? "✓" : "○" }}</span>
                  One special character
                </div>
              </div>
              <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirm Password <span class="required">*</span></label>
              <div class="password-input-container">
                <input
                  id="confirmPassword"
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  maxlength="64"
                  :class="{ error: errors.confirmPassword }"
                  placeholder="Confirm your password"
                  @input="validateConfirmPassword"
                  @blur="validateConfirmPassword"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click.prevent="toggleConfirmPasswordVisibility"
                  :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showConfirmPassword"
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
              <span v-if="errors.confirmPassword" class="error-message">{{
                errors.confirmPassword
              }}</span>
            </div>
          </div>

          <!-- Age Requirement Notice -->
          <div class="age-requirement">
            <div class="age-icon">👤</div>
            <div class="age-text">
              <strong>Age Requirement</strong><br />
              All tutor applicants must be 18 years of age or older. This ensures compliance with
              our platform policies and legal requirements.
            </div>
          </div>
        </div>

        <!-- Academic Background Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Academic Background</h3>
              <p>Your educational qualifications</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="educationLevel">Highest Degree <span class="required">*</span></label>
              <select
                id="educationLevel"
                v-model="educationLevel"
                required
                :class="{ error: errors.educationLevel }"
                @change="validateEducationLevel"
              >
                <option value="">Select your highest degree</option>
                <option value="shs-current">Senior High School (Current)</option>
                <option value="shs-graduate">Senior High School (Graduate)</option>
                <option value="college-undergraduate">College Undergraduate</option>
                <option value="bachelor">Bachelor's Degree</option>
                <option value="master">Master's Degree</option>
                <option value="doctorate">Doctorate/PhD</option>
                <option value="professional">Professional Degree</option>
                <option value="other">Other</option>
              </select>
              <span v-if="errors.educationLevel" class="error-message">{{
                errors.educationLevel
              }}</span>
            </div>

            <div class="form-group">
              <label for="fieldOfStudy"
                >Major / Field of Study <span class="required">*</span></label
              >
              <input
                id="fieldOfStudy"
                v-model="fieldOfStudy"
                type="text"
                required
                maxlength="100"
                :class="{ error: errors.fieldOfStudy }"
                placeholder="e.g., STEM, Biology, Doctorate in IT"
                @input="validateFieldOfStudy"
                @blur="validateFieldOfStudy"
              />
              <span v-if="errors.fieldOfStudy" class="error-message">{{
                errors.fieldOfStudy
              }}</span>
            </div>
          </div>

          <!-- Show "Other" input field when "Other" is selected -->
          <div v-if="educationLevel === 'other'" class="form-group">
            <label for="otherEducationLevel"
              >Please Specify Your Highest Degree <span class="required">*</span></label
            >
            <input
              id="otherEducationLevel"
              v-model="otherEducationLevel"
              type="text"
              required
              :class="{ error: errors.otherEducationLevel }"
              placeholder="Enter your highest degree"
              @input="validateOtherEducationLevel"
              @blur="validateOtherEducationLevel"
            />
            <span v-if="errors.otherEducationLevel" class="error-message">{{
              errors.otherEducationLevel
            }}</span>
          </div>

          <div class="form-group">
            <label for="institution">Institution <span class="required">*</span></label>
            <input
              id="institution"
              v-model="institution"
              type="text"
              required
              maxlength="150"
              :class="{ error: errors.institution }"
              placeholder="Name of your educational institution"
              @input="validateInstitution"
              @blur="validateInstitution"
            />
            <div class="important-note">
              <strong>Important:</strong> Please enter the full name of your educational
              institution. Please avoid abbreviations.
            </div>
            <span v-if="errors.institution" class="error-message">{{ errors.institution }}</span>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="graduationYear">Graduation Year <span class="required">*</span></label>
              <input
                id="graduationYear"
                v-model="graduationYear"
                type="number"
                min="1950"
                max="2035"
                required
                :class="{ error: errors.graduationYear }"
                placeholder="e.g., 2024 or expected year like 2026"
                @input="validateGraduationYear"
                @blur="validateGraduationYear"
              />
              <span v-if="errors.graduationYear" class="error-message">{{
                errors.graduationYear
              }}</span>
            </div>

            <div class="form-group">
              <label for="experienceYears"
                >Years of Teaching Experience <span class="required">*</span></label
              >
              <input
                id="experienceYears"
                v-model="experienceYears"
                type="number"
                min="0"
                max="60"
                required
                :class="{ error: errors.experienceYears }"
                placeholder="Enter 0 if you're a new tutor"
                @input="validateExperienceYears"
                @blur="validateExperienceYears"
              />
              <span v-if="errors.experienceYears" class="error-message">{{
                errors.experienceYears
              }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="subjects">Subjects You Teach <span class="required">*</span></label>
            <div class="multi-select-container" :class="{ error: errors.subjects }">
              <div class="multi-select-header" @click.stop="toggleSubjectsDropdown">
                <div class="selected-subjects-display">
                  <span v-if="selectedSubjects.length === 0" class="placeholder">
                    Select subjects you're qualified to teach
                  </span>
                  <div v-else class="selected-tags">
                    <span
                      v-for="subject in selectedSubjects.slice(0, 2)"
                      :key="subject"
                      class="subject-tag"
                    >
                      {{ subject }}
                      <button type="button" class="remove-tag" @click.stop="removeSubject(subject)">
                        ×
                      </button>
                    </span>
                    <span v-if="selectedSubjects.length > 2" class="more-count">
                      +{{ selectedSubjects.length - 2 }} more
                    </span>
                  </div>
                </div>
                <svg
                  class="dropdown-arrow"
                  :class="{ open: isSubjectsDropdownOpen }"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                </svg>
              </div>
              <div v-if="isSubjectsDropdownOpen" class="multi-select-dropdown">
                <div class="dropdown-search">
                  <input
                    type="text"
                    v-model="subjectSearchQuery"
                    placeholder="Search subjects..."
                    @click.stop
                  />
                </div>
                <div class="dropdown-options">
                  <label
                    v-for="subject in filteredSubjects"
                    :key="subject"
                    class="dropdown-option"
                    :class="{ selected: selectedSubjects.includes(subject) }"
                    @click.stop
                  >
                    <input
                      type="checkbox"
                      :checked="selectedSubjects.includes(subject)"
                      @change="toggleSubject(subject)"
                    />
                    <span class="option-text">{{ subject }}</span>
                  </label>
                  <div v-if="filteredSubjects.length === 0" class="no-results">
                    No subjects found
                  </div>
                </div>
              </div>
            </div>
            <div class="important-note">
              <strong>Important:</strong> Select all SHS subjects you're qualified to teach.
              Validators will review your qualifications.
            </div>
            <span v-if="errors.subjects" class="error-message">{{ errors.subjects }}</span>
          </div>
        </div>
      </div>

      <!-- Continue Button -->
      <div class="button-container">
        <button
          @click="handleContinueClick"
          :disabled="isLoading"
          class="continue-btn"
          :class="{ disabled: !isFormValid }"
        >
          <span v-if="isLoading">Validating...</span>
          <span v-else>Continue to Documents</span>
          <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="global-error">
        {{ errorMessage }}
      </div>

      <!-- Footer Links -->
      <!-- <div class="footer-links">
        <div class="login-link">
          Already have an account?
          <router-link to="/login">Sign in</router-link>
        </div>
        <div class="user-type-link">
          Are you a student?
          <router-link to="/student-register">Register as Student</router-link>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, computed, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { auth, fetchSignInMethodsForEmail, createUserWithEmailAndPassword, db } from "@/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import FileReport from "@/components/FileReport.vue";
import {
  useTutorRegistrationStorage,
  type TutorInfoData,
} from "@/composables/useRegistrationStorage";
import { logDebug } from "@/utils/secureLogger";

const router = useRouter();

// Initialize registration storage
const { saveTutorInfo, loadTutorInfo } = useTutorRegistrationStorage();

// Props to receive initial data
interface Props {
  initialData?: {
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    email: string;
    dateOfBirth: string;
    password: string;
    confirmPassword: string;
    subjects: string;
    experienceYears: string;
    educationLevel: string;
    fieldOfStudy: string;
    institution: string;
    graduationYear: string;
  } | null;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "tutor-info-complete": [
    data: {
      firstName: string;
      middleName: string;
      lastName: string;
      suffix: string;
      email: string;
      dateOfBirth: string;
      password: string;
      confirmPassword: string;
      subjects: string;
      experienceYears: string;
      educationLevel: string;
      fieldOfStudy: string;
      institution: string;
      graduationYear: string;
    }
  ];
}>();

// Form data - Personal Information
const firstName = ref("");
const middleName = ref("");
const lastName = ref("");
const suffix = ref("");
const email = ref("");
const dateOfBirth = ref("");
const password = ref("");
const confirmPassword = ref("");

// Form data - Academic Information
const subjects = ref("");
const experienceYears = ref("");
const educationLevel = ref("");
const otherEducationLevel = ref("");
const fieldOfStudy = ref("");
const institution = ref("");
const graduationYear = ref("");

// SHS Subjects list (same as Student Registration)
const shsSubjects = [
  "Oral Communication",
  "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
  "21st Century Literature from the Philippines and the World",
  "Statistics and Probability",
  "Physical Science",
  "Reading and Writing",
  "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  "General Mathematics",
  "Earth and Life Science",
  "Earth Science",
];

// Subject groupings based on curriculum
const subjectGroups = {
  english: [
    "Oral Communication",
    "Reading and Writing",
    "21st Century Literature from the Philippines and the World",
  ],
  filipino: [
    "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
    "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
  ],
  mathematics: ["General Mathematics", "Statistics and Probability"],
  sciences: ["Earth and Life Science", "Physical Science", "Earth Science"],
};

// Helper function to get the group of a subject
const getSubjectGroup = (subject: string): string | null => {
  for (const [groupName, subjects] of Object.entries(subjectGroups)) {
    if (subjects.includes(subject)) {
      return groupName;
    }
  }
  return null;
};

// Multi-select dropdown state
const selectedSubjects = ref<string[]>([]);
const isSubjectsDropdownOpen = ref(false);
const subjectSearchQuery = ref("");

// State
const isLoading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isFormValid = ref(false);
const showValidationErrors = ref(false); // Track if user tried to submit

// Validation errors
const errors = reactive({
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  email: "",
  dateOfBirth: "",
  password: "",
  confirmPassword: "",
  subjects: "",
  experienceYears: "",
  educationLevel: "",
  otherEducationLevel: "",
  fieldOfStudy: "",
  institution: "",
  graduationYear: "",
});

// Load saved data on mount
onMounted(() => {
  document.title = "ElevatED – Tutor Registration";

  // First try to load from props (when navigating back)
  if (props.initialData) {
    logDebug("Restoring tutor info from props");
    firstName.value = props.initialData.firstName || "";
    middleName.value = props.initialData.middleName || "";
    lastName.value = props.initialData.lastName || "";
    suffix.value = props.initialData.suffix || "";
    email.value = props.initialData.email || "";
    dateOfBirth.value = props.initialData.dateOfBirth || "";
    subjects.value = props.initialData.subjects || "";
    // Parse saved subjects string into array
    if (props.initialData.subjects) {
      selectedSubjects.value = props.initialData.subjects.split(", ").filter((s) => s.trim());
    }
    experienceYears.value = props.initialData.experienceYears || "";
    educationLevel.value = props.initialData.educationLevel || "";
    fieldOfStudy.value = props.initialData.fieldOfStudy || "";
    institution.value = props.initialData.institution || "";
    graduationYear.value = props.initialData.graduationYear || "";
    // Note: passwords are not restored from props for security
  } else {
    // If no props, try to load from sessionStorage
    const savedData = loadTutorInfo();
    if (savedData) {
      logDebug("Restoring tutor info from sessionStorage");
      firstName.value = savedData.firstName || "";
      middleName.value = savedData.middleName || "";
      lastName.value = savedData.lastName || "";
      suffix.value = savedData.suffix || "";
      email.value = savedData.email || "";
      dateOfBirth.value = savedData.dateOfBirth || "";
      // Handle subjects - could be string or array
      if (savedData.subjects) {
        if (Array.isArray(savedData.subjects)) {
          selectedSubjects.value = savedData.subjects;
          subjects.value = savedData.subjects.join(", ");
        } else if (typeof savedData.subjects === "string") {
          subjects.value = savedData.subjects;
          selectedSubjects.value = savedData.subjects.split(", ").filter((s) => s.trim());
        }
      } else {
        subjects.value = "";
        selectedSubjects.value = [];
      }
      experienceYears.value = savedData.experienceYears || "";
      educationLevel.value = savedData.educationLevel || "";
      fieldOfStudy.value = savedData.fieldOfStudy || "";
      institution.value = savedData.institution || "";
      graduationYear.value = savedData.graduationYear || "";
    }
  }

  // Add click outside listener
  document.addEventListener("click", closeDropdownOnClickOutside);
});

// Clean up event listener
onUnmounted(() => {
  document.removeEventListener("click", closeDropdownOnClickOutside);
});

// Auto-save form data to sessionStorage on any change (excluding passwords)
watch(
  [
    firstName,
    middleName,
    lastName,
    suffix,
    email,
    dateOfBirth,
    subjects,
    experienceYears,
    educationLevel,
    fieldOfStudy,
    institution,
    graduationYear,
  ],
  () => {
    const dataToSave: TutorInfoData = {
      firstName: firstName.value,
      middleName: middleName.value,
      lastName: lastName.value,
      suffix: suffix.value,
      email: email.value,
      dateOfBirth: dateOfBirth.value,
      subjects: subjects.value,
      experienceYears: experienceYears.value,
      educationLevel: educationLevel.value,
      fieldOfStudy: fieldOfStudy.value,
      institution: institution.value,
      graduationYear: graduationYear.value,
    };
    saveTutorInfo(dataToSave);
  },
  { deep: true }
);

// Name validation regex (letters, dash, apostrophe only)
const nameRegex = /^[a-zA-Z\s\-']+$/;

// Real-time name validation
const validateNameField = (
  value: string,
  fieldName: keyof typeof errors,
  minLength: number = 2,
  maxLength: number = 50
) => {
  if (!value.trim()) {
    if (fieldName === "firstName" || fieldName === "lastName") {
      errors[fieldName] = `Please enter your ${fieldName === "firstName" ? "First" : "Last"} Name.`;
    }
    return false;
  }

  const trimmedValue = value.trim();

  // Check length
  if (trimmedValue.length < minLength) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name must be at least ${minLength} characters long`;
    return false;
  }

  if (trimmedValue.length > maxLength) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name must be no more than ${maxLength} characters long`;
    return false;
  }

  // Check allowed characters
  if (!nameRegex.test(trimmedValue)) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name can only contain letters, spaces, dashes (-), and apostrophes (')`;
    return false;
  }

  errors[fieldName] = "";
  return true;
};

// Suffix validation (dropdown)
const validateSuffix = () => {
  if (!suffix.value) {
    errors.suffix = "";
    return true; // Suffix is optional
  }

  // If "Other" is selected, we could add additional validation here
  // For now, any selected suffix is valid
  errors.suffix = "";
  return true;
};

// Education level validation
const validateEducationLevel = () => {
  if (!educationLevel.value) {
    errors.educationLevel = "Highest degree is required";
    return false;
  }

  errors.educationLevel = "";
  return true;
};

// Other education level validation (when "Other" is selected)
const validateOtherEducationLevel = () => {
  if (educationLevel.value !== "other") {
    errors.otherEducationLevel = "";
    return true;
  }

  if (!otherEducationLevel.value.trim()) {
    errors.otherEducationLevel = "Please specify your highest degree";
    return false;
  }

  const trimmed = otherEducationLevel.value.trim();

  if (trimmed.length < 2) {
    errors.otherEducationLevel = "Please enter at least 2 characters";
    return false;
  }

  if (trimmed.length > 100) {
    errors.otherEducationLevel = "Maximum 100 characters allowed";
    return false;
  }

  errors.otherEducationLevel = "";
  return true;
};

// Field of study validation (min 2, max 100 chars, letters/dash/period/comma/slash/backslash)
const validateFieldOfStudy = () => {
  if (!fieldOfStudy.value.trim()) {
    errors.fieldOfStudy = "Field of study is required";
    return false;
  }

  const trimmedField = fieldOfStudy.value.trim();

  // Check length
  if (trimmedField.length < 2) {
    errors.fieldOfStudy = "Field of study must be at least 2 characters long";
    return false;
  }

  if (trimmedField.length > 100) {
    errors.fieldOfStudy = "Field of study must be no more than 100 characters long";
    return false;
  }

  // Check allowed characters (letters, spaces, dash, period, comma, slash, backslash)
  const fieldRegex = /^[a-zA-Z\s\-\.,/\\]+$/;
  if (!fieldRegex.test(trimmedField)) {
    errors.fieldOfStudy =
      "Field of study can only contain letters, spaces, dashes (-), periods (.), commas (,), forward slashes (/), and backslashes (\\)";
    return false;
  }

  errors.fieldOfStudy = "";
  return true;
};

// Institution validation (min 4, max 150 chars, letters/numbers/spaces only)
const validateInstitution = () => {
  if (!institution.value.trim()) {
    errors.institution = "Institution is required";
    return false;
  }

  const trimmedInstitution = institution.value.trim();

  // Check length
  if (trimmedInstitution.length < 4) {
    errors.institution = "Institution name must be at least 4 characters long";
    return false;
  }

  if (trimmedInstitution.length > 150) {
    errors.institution = "Institution name must be no more than 150 characters long";
    return false;
  }

  // Check allowed characters (letters, numbers, spaces, apostrophes, commas, hyphens)
  const institutionRegex = /^[a-zA-Z0-9\s',\-]+$/;
  if (!institutionRegex.test(trimmedInstitution)) {
    errors.institution =
      "Institution name can only contain letters, numbers, spaces, apostrophes, commas, and hyphens";
    return false;
  }

  errors.institution = "";
  return true;
};

// Graduation year validation (numeric only, range 1950-2035) - allows expected graduation
const validateGraduationYear = () => {
  if (!graduationYear.value) {
    errors.graduationYear = "Graduation year is required";
    return false;
  }

  const year = parseInt(graduationYear.value);

  // Check if it's a valid number
  if (isNaN(year)) {
    errors.graduationYear = "Please enter a valid year";
    return false;
  }

  // Check year range
  if (year < 1950) {
    errors.graduationYear = "Graduation year must be 1950 or later";
    return false;
  }

  if (year > 2035) {
    errors.graduationYear = "Year must be 2035 or earlier";
    return false;
  }

  errors.graduationYear = "";
  return true;
};

// Experience years validation (numeric only, range 0-60)
const validateExperienceYears = () => {
  const value = experienceYears.value;

  if (value === "" || value === null || value === undefined) {
    errors.experienceYears = "Years of experience is required";
    checkFormValidity();
    return false;
  }

  const years = parseInt(value.toString());

  // Check if it's a valid number
  if (isNaN(years)) {
    errors.experienceYears = "Please enter a valid number";
    checkFormValidity();
    return false;
  }

  // Check range - allow 0 for new tutors
  if (years < 0) {
    errors.experienceYears = "Experience years cannot be negative";
    checkFormValidity();
    return false;
  }

  if (years > 60) {
    errors.experienceYears = "Experience years must be 60 or less";
    checkFormValidity();
    return false;
  }

  errors.experienceYears = "";
  checkFormValidity();
  return true;
};

// Computed: filtered subjects based on search query and subject grouping
const filteredSubjects = computed(() => {
  let availableSubjects = [...shsSubjects];

  // Apply grouping logic if user has selected subjects
  if (selectedSubjects.value.length > 0) {
    // Get the group of the first selected subject
    const selectedGroup = getSubjectGroup(selectedSubjects.value[0]);

    if (selectedGroup) {
      // Only show subjects from the same group
      availableSubjects = subjectGroups[selectedGroup as keyof typeof subjectGroups];
    }
  }

  // Apply search filter if there's a search query
  if (!subjectSearchQuery.value.trim()) {
    return availableSubjects;
  }

  const query = subjectSearchQuery.value.toLowerCase();
  return availableSubjects.filter((subject) => subject.toLowerCase().includes(query));
});

// Toggle subjects dropdown
const toggleSubjectsDropdown = () => {
  isSubjectsDropdownOpen.value = !isSubjectsDropdownOpen.value;
};

// Toggle subject selection
const toggleSubject = (subject: string) => {
  const index = selectedSubjects.value.indexOf(subject);
  if (index > -1) {
    selectedSubjects.value.splice(index, 1);
  } else {
    selectedSubjects.value.push(subject);
  }
  // Update subjects string for backend compatibility
  subjects.value = selectedSubjects.value.join(", ");
  validateSubjects();
};

// Remove subject
const removeSubject = (subject: string) => {
  const index = selectedSubjects.value.indexOf(subject);
  if (index > -1) {
    selectedSubjects.value.splice(index, 1);
  }
  subjects.value = selectedSubjects.value.join(", ");
  validateSubjects();
};

// Close dropdown when clicking outside
const closeDropdownOnClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".multi-select-container")) {
    isSubjectsDropdownOpen.value = false;
  }
};

// Subjects validation (at least 1 subject selected)
const validateSubjects = () => {
  if (selectedSubjects.value.length === 0) {
    errors.subjects = "Please select at least one subject";
    return false;
  }

  errors.subjects = "";
  return true;
};

// Watch for initial data changes and populate form
watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      firstName.value = newData.firstName || "";
      middleName.value = newData.middleName || "";
      lastName.value = newData.lastName || "";
      suffix.value = newData.suffix || "";
      email.value = newData.email || "";
      dateOfBirth.value = newData.dateOfBirth || "";
      password.value = newData.password || "";
      confirmPassword.value = newData.confirmPassword || "";
      subjects.value = newData.subjects || "";
      experienceYears.value = newData.experienceYears || "";
      educationLevel.value = newData.educationLevel || "";
      fieldOfStudy.value = newData.fieldOfStudy || "";
      institution.value = newData.institution || "";
      graduationYear.value = newData.graduationYear || "";
    }
  },
  { immediate: true }
);

// Email validation function using Firestore
const validateEmailExists = async (emailToCheck: string): Promise<boolean> => {
  if (!emailToCheck || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck)) {
    return false;
  }

  try {
    // Check in Firestore users collection
    const usersRef = collection(db, "users");
    const emailQuery = query(usersRef, where("email", "==", emailToCheck));
    const querySnapshot = await getDocs(emailQuery);

    if (!querySnapshot.empty) {
      return true;
    }

    return false;
  } catch (error: any) {
    // If we can't verify (network error, etc.), assume email is available
    // Better to allow registration than block a valid user
    return false;
  }
};

// Real-time confirm password validation
const validateConfirmPassword = () => {
  if (!confirmPassword.value) {
    errors.confirmPassword = "Please confirm your password";
    return;
  }
  errors.confirmPassword = password.value === confirmPassword.value ? "" : "Passwords do not match";
};

// Password visibility toggle functions
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

// Password strength validation
const validatePassword = () => {
  if (!password.value) {
    errors.password = "Password is required";
    return;
  }

  const errorsList: string[] = [];
  if (password.value.length < 8) errorsList.push("min");
  if (password.value.length > 64) errorsList.push("max");
  if (!/[a-z]/.test(password.value)) errorsList.push("low");
  if (!/[A-Z]/.test(password.value)) errorsList.push("up");
  if (!/[0-9]/.test(password.value)) errorsList.push("num");
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password.value)) errorsList.push("spec");

  errors.password = errorsList.length ? "Password does not meet security requirements" : "";
};

// Helper: special char
const hasSpecialChar = (pwd: string): boolean => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd);

// Date of birth validation (20+ for tutors, range 1950-2005)
const validateDateOfBirth = () => {
  if (!dateOfBirth.value) {
    errors.dateOfBirth = "Date of birth is required";
    return;
  }

  const birthDate = new Date(dateOfBirth.value);
  if (isNaN(birthDate.getTime())) {
    errors.dateOfBirth = "Please enter a valid date";
    return;
  }

  const birthYear = birthDate.getFullYear();

  // Check year range (1950 to current year - 18)
  if (birthYear < 1950) {
    errors.dateOfBirth = "Birth year must be 1950 or later";
    return;
  }

  // Calculate age properly
  const today = new Date();
  let age = today.getFullYear() - birthYear;
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;

  // Must be at least 18 years old
  if (age < 18) {
    errors.dateOfBirth = "Must be at least 18 years old to apply";
    return;
  }

  errors.dateOfBirth = "";
};

// Check overall form validity for button state
const checkFormValidity = () => {
  const hasFirst = Boolean(firstName.value.trim() && !errors.firstName);
  const hasLast = Boolean(lastName.value.trim() && !errors.lastName);
  const hasEmail = Boolean(email.value.trim() && !errors.email);
  const dobOk = Boolean(dateOfBirth.value && !errors.dateOfBirth);
  const passOk = Boolean(password.value && !errors.password);
  const confirmOk = Boolean(confirmPassword.value && !errors.confirmPassword);
  const subsOk = Boolean(
    (typeof subjects.value === "string" ? subjects.value.trim() : subjects.value) &&
      !errors.subjects
  );
  const expOk = Boolean(
    experienceYears.value !== "" &&
      experienceYears.value !== null &&
      experienceYears.value !== undefined &&
      !errors.experienceYears &&
      (typeof experienceYears.value === "string" ? experienceYears.value.trim() !== "" : true)
  );
  const eduOk = Boolean(educationLevel.value && !errors.educationLevel);
  const otherEduOk =
    educationLevel.value === "other"
      ? Boolean(otherEducationLevel.value.trim() && !errors.otherEducationLevel)
      : true;
  const fieldOk = Boolean(fieldOfStudy.value.trim() && !errors.fieldOfStudy);
  const instOk = Boolean(institution.value.trim() && !errors.institution);
  const gradOk = Boolean(graduationYear.value && !errors.graduationYear);

  isFormValid.value = Boolean(
    hasFirst &&
      hasLast &&
      hasEmail &&
      dobOk &&
      passOk &&
      confirmOk &&
      subsOk &&
      expOk &&
      eduOk &&
      otherEduOk &&
      fieldOk &&
      instOk &&
      gradOk
  );
};

// Recompute validity on changes
watch(
  [
    firstName,
    lastName,
    email,
    dateOfBirth,
    password,
    confirmPassword,
    subjects,
    experienceYears,
    educationLevel,
    fieldOfStudy,
    institution,
    graduationYear,
  ],
  () => {
    checkFormValidity();
  }
);

// Debounce timer for email validation
let emailValidationTimer: number | null = null;

// Check email existence (shared logic)
const checkEmailAvailability = async () => {
  try {
    const emailExists = await validateEmailExists(email.value);

    if (emailExists) {
      errors.email = "This email is already registered";
    } else {
      // Only clear if there was an "already registered" error
      if (errors.email === "This email is already registered") {
        errors.email = "";
      }
    }

    // Update form validity after email validation
    checkFormValidity();
  } catch (error) {
    // Don't show error for network issues, just log them
  }
};

// Real-time email validation (debounced for typing)
const validateEmail = async () => {
  // Clear existing timer
  if (emailValidationTimer) {
    clearTimeout(emailValidationTimer);
  }

  // Basic validation first
  if (!email.value) {
    errors.email = "Email is required";
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    errors.email = "Please enter a valid email";
    return;
  }

  // Clear format errors but keep any existing "already registered" errors
  if (errors.email === "Please enter a valid email") {
    errors.email = "";
  }

  // Debounce the API call to avoid too many requests while typing
  emailValidationTimer = Number(
    setTimeout(async () => {
      await checkEmailAvailability();
    }, 500)
  ); // Wait 500ms after user stops typing
};

// Email validation on blur (immediate, no debounce)
const validateEmailOnBlur = async () => {
  // Clear any pending debounced validation
  if (emailValidationTimer) {
    clearTimeout(emailValidationTimer);
    emailValidationTimer = null;
  }

  // Basic validation first
  if (!email.value) {
    errors.email = "Email is required";
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    errors.email = "Please enter a valid email";
    return;
  }

  // Clear format errors
  if (errors.email === "Please enter a valid email" || errors.email === "Email is required") {
    errors.email = "";
  }

  // Check email availability immediately
  await checkEmailAvailability();
};

// Validation functions
const validateForm = async () => {
  let isValid = true;

  // Reset errors
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = "";
  });

  // First Name validation
  if (!validateNameField(firstName.value, "firstName")) {
    isValid = false;
  }

  // Last Name validation
  if (!validateNameField(lastName.value, "lastName")) {
    isValid = false;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.value.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!emailRegex.test(email.value)) {
    errors.email = "Please enter a valid email";
    isValid = false;
  } else {
    // Check if email already exists
    const emailExists = await validateEmailExists(email.value);
    if (emailExists) {
      errors.email = "This email is already registered";
      isValid = false;
    }
  }

  // Date of Birth validation
  if (!dateOfBirth.value) {
    errors.dateOfBirth = "Date of birth is required";
    isValid = false;
  }

  // Password validation
  if (!password.value) {
    errors.password = "Password is required";
    isValid = false;
  } else {
    const errorsList: string[] = [];
    if (password.value.length < 8) errorsList.push("min");
    if (password.value.length > 64) errorsList.push("max");
    if (!/[a-z]/.test(password.value)) errorsList.push("low");
    if (!/[A-Z]/.test(password.value)) errorsList.push("up");
    if (!/[0-9]/.test(password.value)) errorsList.push("num");
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password.value)) errorsList.push("spec");

    if (errorsList.length > 0) {
      errors.password = "Password does not meet security requirements";
      isValid = false;
    } else {
      errors.password = ""; // Clear error if password is strong
    }
  }

  // Confirm password validation
  if (!confirmPassword.value) {
    errors.confirmPassword = "Please confirm your password";
    isValid = false;
  } else if (password.value !== confirmPassword.value) {
    errors.confirmPassword = "Passwords do not match";
    isValid = false;
  } else {
    errors.confirmPassword = ""; // Clear error if passwords match
  }

  // Subjects validation
  if (!validateSubjects()) {
    isValid = false;
  }

  // Experience years validation
  if (!validateExperienceYears()) {
    isValid = false;
  }

  // Education level validation
  if (!validateEducationLevel()) {
    isValid = false;
  }

  // Other education level validation (if "Other" is selected)
  if (educationLevel.value === "other" && !validateOtherEducationLevel()) {
    isValid = false;
  }

  // Field of study validation
  if (!validateFieldOfStudy()) {
    isValid = false;
  }

  // Institution validation
  if (!validateInstitution()) {
    isValid = false;
  }

  // Graduation year validation
  if (!validateGraduationYear()) {
    isValid = false;
  }

  return isValid;
};

// Handle continue button click (even when disabled)
const handleContinueClick = async () => {
  if (!isFormValid.value) {
    // Show validation errors
    showValidationErrors.value = true;

    // Run full validation to populate error messages
    await validateForm();

    // Scroll to first error
    setTimeout(() => {
      const firstError = document.querySelector(
        ".error-message:not(:empty), input.border-red-500, select.border-red-500"
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return;
  }

  // If form is valid, proceed with submission
  await handleSubmit();
};

// Handle form submission
const handleSubmit = async () => {
  isLoading.value = true;
  errorMessage.value = "";

  // Validate form (async - checks email in Firestore)
  const isValid = await validateForm();
  if (!isValid) {
    isLoading.value = false;
    return;
  }

  try {
    // Just emit the data to parent component
    const formData = {
      firstName: firstName.value,
      middleName: middleName.value,
      lastName: lastName.value,
      suffix: suffix.value,
      email: email.value,
      dateOfBirth: dateOfBirth.value,
      password: password.value,
      confirmPassword: confirmPassword.value,
      subjects: subjects.value,
      experienceYears: experienceYears.value,
      educationLevel: educationLevel.value,
      fieldOfStudy: fieldOfStudy.value,
      institution: institution.value,
      graduationYear: graduationYear.value,
    };

    emit("tutor-info-complete", formData);
  } catch (error: any) {
    errorMessage.value = "An error occurred. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

// Handle back button click
const handleBackClick = () => {
  router.push("/register-role");
};
</script>

<style scoped>
.registration-container {
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
}

.element-1 {
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}

.element-2 {
  top: 25%;
  right: 15%;
  animation-delay: 1s;
}

.element-3 {
  top: 45%;
  left: 5%;
  animation-delay: 2s;
}

.element-4 {
  top: 65%;
  right: 8%;
  animation-delay: 3s;
}

.element-5 {
  top: 80%;
  left: 20%;
  animation-delay: 4s;
}

.element-6 {
  top: 35%;
  right: 25%;
  animation-delay: 5s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.progress-header {
  padding: 40px 20px 0px;
  padding-top: 160px; /* Increased top padding to avoid nav bar overlap */
  text-align: center;
  position: relative;
  margin-bottom: 0px;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
}

.title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5em;
  margin-right: 7em;
}

.back-button {
  width: 60px;
  height: 60px;
  background: white;
  border: 3px solid #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  color: #000000;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #000000;
}

.back-button svg {
  width: 30px;
  height: 30px;
}

.title-content {
  text-align: center;
}

.main-title {
  font-size: 40px;
  font-weight: 700;
  color: #000000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.subtitle {
  font-size: 18px;
  color: #333333;
  margin: 0 0 40px 0;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  margin: 0px 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 50px;
  height: 50px;
  border: 3px solid #000000;
  border-radius: 50%;
  background: #888888;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.3s ease;
  padding: 4px;
}

.step:first-child .step-number {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step:first-child .step-number:hover {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step.active .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

.step.completed .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

.step-label {
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
  max-width: 120px;
  line-height: 1.3;
}

.step.active .step-label {
  color: #000000;
  font-weight: 700;
}

.step.completed .step-label {
  color: #000000;
  font-weight: 600;
}

.step-line {
  width: 60px;
  height: 4px;
  background: #000000;
  margin: 0 8px;
  border-radius: 2px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 40px;
}

.form-card {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  width: 100%;
  max-width: 1000px;
  margin: 20px auto 20px auto; /* Proper spacing to prevent cutting */
}

.form-section {
  background: white;
  border: 3px solid #090909;
  overflow: visible;
  border-radius: 16px;
  padding: 36px;
  margin-bottom: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: none;
  background-color: transparent;
  border-radius: 0;
  margin: -36px -36px 16px -36px;
}

.section-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.section-icon svg {
  width: 28px;
  height: 28px;
}

.section-title h3 {
  margin: 0;
  color: #1e40af;
  font-size: 20px;
  font-weight: 600;
}

.section-title p {
  margin: 4px 0 0 0;
  color: #3b82f6;
  font-size: 14px;
  font-style: normal;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: white;
  font-family: inherit;
  font-weight: 500;
}

.form-group select {
  padding-right: 40px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
}

.form-group input::placeholder,
.form-group select::placeholder,
.form-group textarea::placeholder {
  color: #9ca3af;
  opacity: 1;
  font-weight: 400;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.important-note {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  color: #1e40af;
  padding: 16px;
  border-radius: 12px;
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
}

.age-requirement {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.age-icon {
  font-size: 24px;
  width: 40px;
  height: 40px;
  background: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.age-text {
  color: #1e40af;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
}

.password-requirements {
  margin-top: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.requirement:last-child {
  margin-bottom: 0;
}

.requirement.valid {
  color: #059669;
}

.checkmark {
  font-weight: 700;
  font-size: 16px;
}

.error-message {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
  display: block;
  font-weight: 500;
}

/* Multi-select dropdown styles */
.multi-select-container {
  position: relative;
  width: 100%;
}

.multi-select-container.error .multi-select-header {
  border-color: #ef4444;
}

.multi-select-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 56px;
}

.multi-select-header:hover {
  border-color: #4f46e5;
}

.selected-subjects-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.placeholder {
  color: #94a3b8;
  font-size: 16px;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.subject-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #556fff;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.remove-tag {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  transition: opacity 0.2s;
}

.remove-tag:hover {
  opacity: 0.7;
}

.more-count {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
}

.dropdown-arrow {
  width: 24px;
  height: 24px;
  color: #64748b;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.multi-select-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dropdown-search {
  padding: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.dropdown-search input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.dropdown-search input:focus {
  outline: none;
  border-color: #4f46e5;
}

.dropdown-options {
  overflow-y: auto;
  max-height: 300px;
  padding: 8px;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  position: relative;
  user-select: none;
}

.dropdown-option::before,
.dropdown-option::after {
  content: none !important;
}

.dropdown-option:hover {
  background-color: #f1f5f9;
}

.dropdown-option.selected {
  background-color: #e9e7fd;
  color: #556fff;
}

.dropdown-option.selected .option-text {
  color: #556fff;
  font-weight: 600;
}

.dropdown-option input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
}

.dropdown-option .checkmark {
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
  background: white;
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;
}

.dropdown-option input[type="checkbox"]:checked ~ .checkmark {
  background: #4f46e5;
  border-color: #4f46e5;
}

.dropdown-option input[type="checkbox"]:checked ~ .checkmark::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.option-text {
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  user-select: none;
}

.option-text::before {
  content: none !important;
}

.no-results {
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.global-error {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  color: #dc2626;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  text-align: center;
  font-weight: 600;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 24px;
}

.continue-btn {
  width: auto;
  min-width: 250px;
  padding: 18px 32px;
  background: #4f46e5;
  color: white;
  border: 3px solid #000000;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 4px 4px 0px #000000;
}

.continue-btn:hover:not(:disabled):not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 6px 6px 0px #000000;
}

.continue-btn:disabled,
.continue-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #9ca3af;
  border-color: #6b7280;
  box-shadow: 2px 2px 0px #6b7280;
  transform: none;
}

.arrow-icon {
  width: 24px;
  height: 24px;
}

/* Password visibility toggle styles */
.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-container input {
  padding-right: 50px;
}

.password-toggle {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: color 0.2s ease;
  border-radius: 4px;
  z-index: 10;
  pointer-events: auto;
}

.password-toggle:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
}

.eye-icon {
  width: 20px;
  height: 20px;
}

.footer-links {
  margin-top: 32px;
  text-align: center;
}

.login-link {
  text-align: center;
  color: #6b7280;
  font-size: 15px;
  margin-bottom: 16px;
  font-weight: 500;
}

.login-link a {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
}

.login-link a:hover {
  text-decoration: underline;
}

.user-type-link {
  text-align: center;
  color: #6b7280;
  font-size: 15px;
  font-weight: 500;
}

.user-type-link a {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
}

.user-type-link a:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .progress-header {
    padding: 30px 16px 20px;
    padding-top: 100px;
  }

  .title-section {
    gap: 16px;
    margin-bottom: 24px;
  }

  .back-button {
    width: 36px;
    height: 36px;
  }

  .back-button svg {
    width: 18px;
    height: 18px;
  }

  .main-title {
    font-size: 2rem;
    margin-bottom: 12px;
  }

  .subtitle {
    font-size: 1rem;
    margin-bottom: 24px;
  }

  .step-number {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .step-line {
    width: 50px;
  }

  .content-wrapper {
    padding: 20px 16px 40px;
  }

  .form-card {
    padding: 32px 24px;
    margin: 10px;
  }

  .section-header {
    gap: 16px;
  }

  .section-icon {
    width: 48px;
    height: 48px;
  }

  .section-icon svg {
    width: 24px;
    height: 24px;
  }

  .section-title h3 {
    font-size: 20px;
  }

  .section-title p {
    font-size: 14px;
  }

  .floating-element {
    font-size: 20px;
  }

  .button-container {
    justify-content: stretch;
    margin-top: 20px;
    margin-bottom: 20px;
    max-width: none;
  }

  .validation-warning {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border: 2px solid #fca5a5;
    color: #dc2626;
    padding: 16px 20px;
    border-radius: 12px;
    margin-top: 16px;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .continue-btn {
    width: 100%;
    min-width: auto;
    padding: 16px 24px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .progress-header {
    padding: 20px 12px 16px;
    padding-top: 80px;
  }

  .title-section {
    gap: 12px;
    margin: 0 3em 0 1em !important;
    margin-bottom: 20px;
  }

  .back-button {
    width: 32px;
    height: 32px;
  }

  .back-button svg {
    width: 16px;
    height: 16px;
  }

  .main-title {
    font-size: 1.75rem;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .step-progress {
    gap: 12px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .step-line {
    width: 40px;
  }

  .content-wrapper {
    padding: 16px 12px 32px;
  }

  .form-card {
    padding: 24px 20px;
    margin: 5px;
  }

  .form-section {
    padding: 24px 20px;
    margin-bottom: 24px;
  }

  .section-header {
    gap: 12px;
    margin-bottom: 24px;
  }

  .section-icon {
    width: 40px;
    height: 40px;
  }

  .section-icon svg {
    width: 20px;
    height: 20px;
  }

  .section-title h3 {
    font-size: 18px;
  }

  .section-title p {
    font-size: 13px;
  }

  .form-grid {
    gap: 16px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .floating-element {
    font-size: 16px;
  }

  .button-container {
    justify-content: stretch;
    margin-top: 16px;
    margin-bottom: 16px;
    max-width: none;
  }

  .continue-btn {
    width: 100%;
    min-width: auto;
    padding: 14px 20px;
    font-size: 16px;
  }
}
</style>
